/* eslint-disable no-nested-ternary */
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PageTitle } from 'components/layouts/pageTitle';
import { ConfirmationDialog, ConfirmationDialogTypes } from 'components/lists/confirmationDialog';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { ImageUpload } from 'components/formInputs/imageUpload';
import { files } from 'codemash';
import { FormButton } from 'components/buttons/formButton';
import { formFileUrlFromFileArray } from 'utils/imageHelper';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';

export const DriversAddEditForm = ({ itemData, isEdit, initialRestaurant }) => {
  const navigate = useNavigate();

  const {
    state: companyState,
    addNew,
    editById,
    deleteById,
    getCompanyRestaurants,
  } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);

  const [profileImage, setProfileImage] = useState([]);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    first_name: yup
      .string()
      .required(
        'Please enter a first name for the driver',
      )
      .default(itemData?.first_name),
    last_name: yup
      .string()
      .required(
        'Please enter a last name for the driver',
      )
      .default(itemData?.last_name),
    /*
    phone_number: yup.string().default(itemData?.phone_number),
    external_id: yup.string().default(itemData?.external_id),
    external_name: yup.string().default(itemData?.external_name),  */
    restaurants: yup
      .array()
      .of(yup.string())
      .required('Please enter restaurants.')
      .min(1, 'Please select at least one restaurant.'),
  });

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(itemData);

    if (itemData?.image) {
      setProfileImage([{ data_url: formFileUrlFromFileArray(itemData?.image, true) }]);
    }

    setSelectedRestaurants(itemData ? (itemData?.restaurants || []) : initialRestaurant ? [initialRestaurant] : []);

    if (!itemData && initialRestaurant) {
      setValue('restaurants', [initialRestaurant]);
    }
  }, [itemData, reset]);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onSubmit = async (data) => {
    const sumbitData = {
      company: auth.company?.company,
      ...data,
    };

    setIsLoading(true);

    if (!isEdit) {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.DRIVERS,
          data: sumbitData,
          token: auth.token,
        },
        async (newId) => {
          if (profileImage && profileImage.length > 0) {
            await files.uploadRecordFile({
              collectionName: Config.DATA.COLLECTIONS.DRIVERS,
              database: Config.CODEMASH.DATABASE_ID,
              recordId: newId,
              uniqueFieldName: 'image',
              secretKey: auth.token,
              file: profileImage[0].file,
            });
          }

          navigate('/settings/drivers');
        },
        () => {
          setIsLoading(false);
        },
      );
    } else {
      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.DRIVERS,
          id: data._id,
          updateClause: {
            $set: {
              first_name: data.first_name,
              last_name: data.last_name,
              /*
              email: data.email,
              personal_number: data.personal_number,
              phone_number: data.phone_number,
              bank_account: data.bank_account,
              external_id: data.external_id,
              external_name: data.external_name,  */
            },
          },
          token: auth.token,
        },
        async () => {
          if (profileImage && profileImage.length > 0) {
            const uploadedNewImage =
              profileImage[0].file !== null && profileImage[0].file !== undefined;

            if (uploadedNewImage) {
              await files.uploadRecordFile({
                collectionName: Config.DATA.COLLECTIONS.DRIVERS,
                database: Config.CODEMASH.DATABASE_ID,
                recordId: sumbitData._id,
                uniqueFieldName: 'image',
                secretKey: auth.token,
                file: profileImage[0].file,
              });
            }
          } else {
            await editById({
              collectionName: Config.DATA.COLLECTIONS.DRIVERS,
              database: Config.CODEMASH.DATABASE_ID,
              id: sumbitData._id,
              updateClause: { $set: { image: null } },
              token: auth.token,
            });
          }

          navigate('/settings/drivers');
        },
        () => {
          setIsLoading(false);
        },
      );
    }
  };
  const onError = (data) => console.log(data);

  const onDelete = async () => {
    await deleteById({
      collectionName: Config.DATA.COLLECTIONS.DRIVERS,
      token: auth.token,
      id: itemData._id,
    }, () => navigate('/settings/drivers'));
  };

  const getRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-', address: '' }, ...companyState.restaurants];

    return allRestaurants
      ?.filter((r) => !selectedRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: `${restaurant.name} ${restaurant.address ? `(${restaurant.address})` : ''}`,
        value: restaurant._id,
      }));
  };

  const deleteSelectedRestaurant = (restaurantId) => {
    const newRestaurants = selectedRestaurants.filter((r) => r !== restaurantId);

    setSelectedRestaurants(newRestaurants);
    setValue('restaurants', newRestaurants);
  };

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <div>
        {selectedRestaurants.map((restaurant) => {
          const restaurantData = companyState.restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurant} className="mt-2">
              <SelectedItemButton
                key={restaurantData._id}
                name={restaurantData?.name}
                onClick={() => deleteSelectedRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <PageTitle
        title="Driver"
        breadcrumbs={[{ path: '/settings/drivers', title: 'Drivers' }]}
      />
      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {isEdit
                      ? `Editing driver: ${itemData?.first_name} ${itemData?.last_name}`
                      : 'Add new driver'}
                  </h3>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <DropDownList
                      showLabel
                      id="restaurants"
                      name="restaurants"
                      label="Restaurants"
                      options={getRestaurantsDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      onChange={(e) => {
                        setSelectedRestaurants((oldArray) => [...oldArray, e.target.value]);
                        setValue('restaurants', [...selectedRestaurants, e.target.value]);
                      }}
                    />
                    {errors?.restaurants && (
                    <p className="mt-2 text-sm text-red-600" id="email-error">
                      {errors?.restaurants.message}
                    </p>
                    )}
                    {renderSelectedRestaurants()}
                  </div>
                  <div className="hidden sm:block sm:col-span-3" />

                  <TextboxWrapper
                    register={register}
                    name="first_name"
                    title="First name*"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.first_name}
                  />

                  <TextboxWrapper
                    register={register}
                    name="last_name"
                    title="Last name*"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.last_name}
                  />

                  <div className="sm:col-span-3 col-span-6">
                    <label htmlFor="profileImage" className="block text-sm font-medium text-gray-700 mb-1">
                      Profile photo
                    </label>
                    <ImageUpload id="profileImage" name="profileImage" images={profileImage} onChange={(val) => setProfileImage(val)} />
                  </div>

                  {/*
                  <TextboxWrapper
                    register={register}
                    name="email"
                    title="Email"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.email}
                  />

                  <TextboxWrapper
                    register={register}
                    name="personal_number"
                    title="Personal no."
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.personal_number}
                  />

                  <TextboxWrapper
                    register={register}
                    name="phone_number"
                    title="Phone no."
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.phone_number}
                  />

                  <TextboxWrapper
                    register={register}
                    name="bank_account"
                    title="Bank account"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.bank_account}
                  /> */}

                  {/* <div className="col-span-6">
                    <fieldset className="mt-6">
                      <div className="mb-6">
                        <legend className="text-base font-medium text-gray-900">External driver (optional)</legend>
                        <p className="text-sm text-gray-500 mt-1">
                          Driver identifiers from integration, only used for &quot;Pay for orders&quot; type restaurants
                        </p>
                      </div>
                      <div className="grid grid-cols-6 gap-6">
                        <DropdownWrapper
                          name="waiter_fill"
                          title="Select waiter to fill"
                          options={[{ label: '-', value: '' }, ...(externalWaiters?.map((x) => ({ label: x.name, value: x._id })) || [])]}
                          responsiveClassName="col-span-6 sm:col-span-3"
                          onChange={(e) => {
                            const waiterData = externalWaiters?.find((x) => x._id === e.target.value);

                            setValue('external_id', waiterData?._id);
                            setValue('external_name', waiterData?.name);
                          }}
                        />
                        <div className="col-span-6 sm:col-span-3" />

                        <TextboxWrapper
                          register={register}
                          name="external_id"
                          title="External ID"
                          responsiveClassName="col-span-6 sm:col-span-3"
                          error={errors?.external_id}
                        />
                        <TextboxWrapper
                          register={register}
                          name="external_name"
                          title="External name"
                          responsiveClassName="col-span-6 sm:col-span-3"
                          error={errors?.external_name}
                        />
                      </div>
                    </fieldset>
                  </div> */}

                  <div className="col-span-4 sm:col-span-0 lg:col-span-4" />
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-between items-start">
                {isEdit && (
                <button
                  type="button"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  Delete
                </button>
                )}
                {!isEdit && <div />}
                <div className="text-right">
                  <button
                    type="button"
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => navigate('/settings/drivers')}
                  >
                    Cancel
                  </button>
                  <FormButton text="Save" isLoading={isLoading} />
                </div>
              </div>

            </div>
          </form>
        </div>
      </div>
      <ConfirmationDialog
        type={ConfirmationDialogTypes.DELETE}
        title="Delete driver"
        description="Are you sure you want to delete the driver?"
        isOpen={isDeleteDialogOpen}
        closeModal={() => setIsDeleteDialogOpen(false)}
        onSubmit={onDelete}
      />
    </>
  );
};
